import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import { FuseVerticalNavigationComponent } from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { NavigationService } from "app/core/navigation/navigation.service";
import { AuthService } from "app/core/auth/auth.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { NotificationsComponent } from "app/layout/common/notifications/notifications.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { environment } from "../../../../../environments/environment";

import { Subject, takeUntil } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "classic-layout",
  templateUrl: "./classic.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    LanguagesComponent,
    NotificationsComponent,
    UserComponent,
    RouterOutlet,
    NgIf,
  ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
  @ViewChild(FuseVerticalNavigationComponent)
  private _navigationComponent?: FuseVerticalNavigationComponent;

  isScreenSmall: boolean;
  navigation: Navigation = null;
  readonly appVersion = environment.appVersion;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private destroyRef: DestroyRef,
    private _authService: AuthService,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }
  userDetails: any = {};

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this.userDetails = this._authService.getUser();

    // Get the navigation directly from the service
    this._navigationService.navigation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((navigation) => {
        const nav =
          typeof structuredClone === "function"
            ? structuredClone(navigation)
            : JSON.parse(JSON.stringify(navigation));

        // Work on the "default" layout group (index 0)
        const group = { ...nav.default?.[0] };
        const children = Array.isArray(group.children)
          ? [...group.children]
          : [];

        if (this.userDetails?.role === "staff") {
          // Keep only the "bookings" item
          const bookings = children.find((c) => c?.id === "bookings") ?? {
            id: "bookings",
            title: "Bookings",
            type: "basic",
            icon: "calendar_today",
            link: "/bookings",
          };

          group.children = [bookings];
        } else {
          // Non-staff: use the navigation as is (with venues if they exist in the navigation config)
          group.children = children;
        }

        // Write back immutably
        nav.default = [...(nav.default ?? [])];
        nav.default[0] = group;

        this.navigation = nav; // new reference => re-render
      });

    // Media watcher
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matchingAliases }) => {
        this.isScreenSmall = !matchingAliases.includes("md");
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(): void {
    this._navigationComponent?.toggle();
  }
}
